import React, { useState, useEffect, useCallback } from 'react';
import {
  Show,
  useDataProvider,
  useNotify,
  useRedirect,
  useRecordContext,
  Button as RaButton,
  CreateButton,
  EditButton,
} from 'react-admin';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Chip,
  Divider,
  Button,
  IconButton,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Avatar,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Assignment as TaskIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  ArrowForward as ArrowForwardIcon,
  ContactPhone as ContactPhoneIcon,
  ContactMail as ContactMailIcon,
  Place as ZoneIcon,
  AttachFile as AttachFileIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import ResourceBreadcrumbsWithActions from '../../components/ResourceBreadcrumbsWithActions';
import { getCustomerTypeColor } from '../../utils/customerTypeColors';
import { GoogleMap, useJsApiLoader, MarkerF } from '@react-google-maps/api';
import { LocationFileUploadDialog } from '../../components/LocationFileUploadDialog';

const GOOGLE_MAPS_API_KEY = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string) || '';
const libraries: ("places" | "drawing" | "geometry" | "localContext" | "visualization")[] = ['places'];

const mapContainerStyle = {
  width: '100%',
  height: '300px',
};

export const SitesShow: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();
  
  const [location, setLocation] = useState<any>(null);
  const [customer, setCustomer] = useState<any>(null);
  const [tasks, setTasks] = useState<any[]>([]);
  const [locationFiles, setLocationFiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filesLoading, setFilesLoading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  // Check if Google Maps is already loaded
  const { isLoaded } = useJsApiLoader({
    id: 'google-maps-script-sites',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: libraries,
  });

  // Load location files
  const loadLocationFiles = useCallback(async () => {
    if (!id) return;

    try {
      setFilesLoading(true);
      const filesResponse = await dataProvider.getList(`crm/locations/${id}/files`, {
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'createdAt', order: 'DESC' },
        filter: {},
      });
      setLocationFiles(filesResponse.data);
    } catch (error) {
      console.error('Error loading location files:', error);
      setLocationFiles([]);
    } finally {
      setFilesLoading(false);
    }
  }, [id, dataProvider]);

  // Delete a file
  const handleDeleteFile = async (fileId: string) => {
    if (!id || !fileId) return;

    if (!window.confirm('Are you sure you want to delete this file?')) {
      return;
    }

    try {
      await dataProvider.delete(`crm/locations/${id}/files`, { id: fileId });
      notify('File deleted successfully', { type: 'success' });
      // Reload files list
      await loadLocationFiles();
    } catch (error) {
      console.error('Error deleting file:', error);
      notify('Error deleting file', { type: 'error' });
    }
  };

  // Download/preview a file
  const handleFileClick = async (file: any) => {
    if (!file.file?.id) return;

    try {
      // Get the file URL from the backend
      const fileResponse = await dataProvider.getOne('files', { id: file.file.id });
      const fileData = fileResponse.data;

      if (fileData.url) {
        // Open the file in a new tab
        window.open(fileData.url, '_blank');
      } else {
        notify('File URL not available', { type: 'error' });
      }
    } catch (error) {
      console.error('Error accessing file:', error);
      notify('Error accessing file', { type: 'error' });
    }
  };

  // Load location details
  const loadLocationDetails = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);

      // Load location from the API
      const locationResponse = await dataProvider.getOne('crm/locations', { id });
      const locationData = locationResponse.data;

      setLocation(locationData);

      // Load files for this location
      await loadLocationFiles();
      
      // Get customer from embedded data
      let customerData = null;
      
      // Check if location has embedded customerLocations
      if (locationData.customerLocations && locationData.customerLocations.length > 0) {
        // Get the first (or primary) customer
        const primaryCustomerLocation = locationData.customerLocations.find((cl: any) => cl.isPrimary) || 
                                       locationData.customerLocations[0];
        customerData = primaryCustomerLocation.customer;
      } else if (locationData.customerId) {
        // Fallback: If location has a direct customerId but no embedded data
        try {
          const customerResponse = await dataProvider.getOne('crm/customers', { id: locationData.customerId });
          customerData = customerResponse.data;
        } catch (error) {
          console.error(`Error loading customer ${locationData.customerId}:`, error);
        }
      }
      
      setCustomer(customerData);
      
      // Load tasks for this location and customer
      if (customerData) {
        try {
          const tasksResponse = await dataProvider.getList('tasks', {
            filter: {
              customerId: customerData.id,
            },
            sort: { field: 'dueDate', order: 'ASC' },
            pagination: { page: 1, perPage: 100 },
          });
          setTasks(tasksResponse.data);
        } catch (error) {
          console.error('Error loading tasks:', error);
        }
      }
      
    } catch (error) {
      notify('Error loading site details', { type: 'error' });
      console.error('Error loading site details:', error);
    } finally {
      setLoading(false);
    }
  }, [id, dataProvider, notify, loadLocationFiles]);

  useEffect(() => {
    loadLocationDetails();
  }, [loadLocationDetails]);

  const handleCreateTask = () => {
    if (customer) {
      redirect(`/tasks/create?customerId=${customer.id}&locationId=${id}`);
    }
  };

  const handleViewCustomer = () => {
    if (customer) {
      redirect(`/customers/${customer.id}/show`);
    }
  };

  const getTaskStatusIcon = (status: string | undefined | null) => {
    if (!status) return <TaskIcon />;
    switch (status.toLowerCase()) {
      case 'completed':
      case 'done':
        return <CheckCircleIcon color="success" />;
      case 'inprogress':
      case 'in_progress':
        return <ScheduleIcon color="primary" />;
      case 'todo':
      case 'pending':
        return <WarningIcon color="warning" />;
      default:
        return <TaskIcon />;
    }
  };

  const getTaskPriorityColor = (priority: string | undefined | null) => {
    if (!priority) return 'default';
    switch (priority.toLowerCase()) {
      case 'high':
      case 'urgent':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!location) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">Site not found</Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/sites')}
          sx={{ mt: 2 }}
        >
          Back to Sites
        </Button>
      </Box>
    );
  }

  const hasCoordinates = location.latitude && location.longitude;
  const mapCenter = {
    lat: hasCoordinates ? Number(location.latitude) : 43.6532,
    lng: hasCoordinates ? Number(location.longitude) : -79.3832,
  };


  return (
    <Show resource="crm/locations" id={id} title={location?.name || location?.streetLine1 || 'Loading...'}>
      <ResourceBreadcrumbsWithActions 
        customNames={{ singular: 'Site', plural: 'Sites' }}
        hideEdit={true}
        listUrl="/sites"
      />
      
      <Grid container spacing={3}>
        {/* Left Side - Location and Customer Details */}
        <Grid item xs={12} md={6}>
          {/* Location Details Card */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <LocationIcon color="primary" />
                <Typography variant="h6">
                  Location Details
                </Typography>
              </Box>
              
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                {location.name || location.streetLine1}
              </Typography>
              
              <Typography variant="body2" color="text.secondary">
                {location.streetLine1}
                {location.streetLine2 && `, ${location.streetLine2}`}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {location.city}, {location.province} {location.postalCode}
              </Typography>

              {/* Zone Information */}
              {location.zone && (
                <Box sx={{ mb: 2 }}>
                  <Divider sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">Service Zone</Typography>
                  </Divider>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <ZoneIcon fontSize="small" color="action" />
                    {location.zone.name}
                  </Typography>
                </Box>
              )}

              {/* Contact Information */}
              {(location.contactName || location.contactPhone || location.contactEmail) && (
                <Box sx={{ mb: 2 }}>
                  <Divider sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">Contact Information</Typography>
                  </Divider>
                  {location.contactName && (
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <PersonIcon fontSize="small" color="action" />
                      <span style={{ fontWeight: 500 }}>Contact:</span> {location.contactName}
                    </Typography>
                  )}
                  {location.contactPhone && (
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <ContactPhoneIcon fontSize="small" color="action" />
                      <span style={{ fontWeight: 500 }}>Phone:</span> {location.contactPhone}
                    </Typography>
                  )}
                  {location.contactEmail && (
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <ContactMailIcon fontSize="small" color="action" />
                      <span style={{ fontWeight: 500 }}>Email:</span> {location.contactEmail}
                    </Typography>
                  )}
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <Chip
                  label={location.isActive || location.active ? 'Active' : 'Inactive'}
                  color={(location.isActive || location.active) ? 'success' : 'default'}
                  size="small"
                />
                <Chip
                  label={`${tasks.length} Task${tasks.length !== 1 ? 's' : ''}`}
                  size="small"
                />
                <Chip
                  label={`${locationFiles.length} File${locationFiles.length !== 1 ? 's' : ''}`}
                  size="small"
                />
              </Box>

              {/* Notes */}
              {location.notes && (
                <Box sx={{ mt: 2 }}>
                  <Divider sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">Notes</Typography>
                  </Divider>
                  <Typography variant="body2" sx={{
                    backgroundColor: 'grey.50',
                    padding: 1.5,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'grey.200',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {location.notes}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Customer Details Card */}
          {customer ? (
            <Card 
              sx={{ 
                cursor: 'pointer',
                '&:hover': { boxShadow: 3 }
              }}
              onClick={handleViewCustomer}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <BusinessIcon color="primary" />
                  <Typography variant="h6">
                    Customer Details
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      {customer.displayName}
                    </Typography>
                    
                    {customer.customerType && (
                      <Chip
                        label={customer.customerType.name}
                        size="small"
                        sx={{
                          mb: 1,
                          backgroundColor: customer.customerType.color || getCustomerTypeColor(customer.customerType.name),
                          color: 'white'
                        }}
                      />
                    )}
                    
                    {customer.company && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {customer.company}
                      </Typography>
                    )}
                    
                    <Box sx={{ mt: 2 }}>
                      {customer.phones && customer.phones.length > 0 && (
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                          <PhoneIcon fontSize="small" color="action" />
                          {customer.phones.find((p: any) => p.isPrimary)?.phone || customer.phones[0].phone}
                        </Typography>
                      )}
                      {customer.emails && customer.emails.length > 0 && (
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <EmailIcon fontSize="small" color="action" />
                          {customer.emails.find((e: any) => e.isPrimary)?.email || customer.emails[0].email}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <IconButton>
                    <ArrowForwardIcon />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <BusinessIcon color="primary" />
                  <Typography variant="h6">
                    Customer Details
                  </Typography>
                </Box>
                <Typography variant="body1" color="text.secondary">
                  No customer assigned to this location
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Right Side - Map */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', minHeight: '400px' }}>
            <CardContent sx={{ p: 0, height: '100%', '&:last-child': { pb: 0 } }}>
              {isLoaded ? (
                <GoogleMap
                  mapContainerStyle={{ width: '100%', height: '100%', minHeight: '400px' }}
                  center={mapCenter}
                  zoom={15}
                  options={{
                    zoomControl: true,
                    streetViewControl: true,
                    mapTypeControl: true,
                    fullscreenControl: true,
                  }}
                >
                  <MarkerF 
                    position={mapCenter}
                  />
                </GoogleMap>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                  <CircularProgress />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tasks Section */}
      <Paper sx={{ p: 2, mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Tasks ({tasks.length})
          </Typography>
          {customer && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateTask}
            >
              Create Task
            </Button>
          )}
        </Box>
        
        {/* Tasks List */}
        {tasks.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
            <TaskIcon sx={{ fontSize: 48, mb: 2 }} />
            <Typography variant="body1">
              No tasks for this location
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {tasks.map((task) => (
              <ListItem
                key={task.id}
                sx={{
                  mb: 1,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': { bgcolor: 'action.hover' },
                  cursor: 'pointer',
                }}
                onClick={() => redirect(`/tasks/${task.id}/show`)}
              >
                <ListItemIcon>
                  {getTaskStatusIcon(task.status)}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1">{task.title}</Typography>
                      <Chip
                        label={task.priority}
                        size="small"
                        color={getTaskPriorityColor(task.priority) as any}
                      />
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 0.5 }}>
                      {task.dueDate && (
                        <Typography variant="body2" color="text.secondary">
                          <CalendarIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                          Due: {new Date(task.dueDate).toLocaleDateString()}
                        </Typography>
                      )}
                      {task.assignee && (
                        <Typography variant="body2" color="text.secondary">
                          <PersonIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                          Assigned to: {task.assignee.name}
                        </Typography>
                      )}
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton edge="end" onClick={(e) => {
                    e.stopPropagation();
                    redirect(`/tasks/${task.id}`);
                  }}>
                    <EditIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      {/* Files Section */}
      <Paper sx={{ p: 2, mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Files ({locationFiles.length})
          </Typography>
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={() => setUploadDialogOpen(true)}
          >
            Upload File
          </Button>
        </Box>

        {/* Files List */}
        {filesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : locationFiles.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
            <AttachFileIcon sx={{ fontSize: 48, mb: 2 }} />
            <Typography variant="body1">
              No files attached to this location
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {locationFiles.map((file) => (
              <ListItem
                key={file.id}
                sx={{
                  mb: 1,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': { bgcolor: 'action.hover' },
                  cursor: 'pointer',
                }}
                onClick={() => handleFileClick(file)}
              >
                <ListItemIcon>
                  <AttachFileIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body1">{file.file?.name || 'Unknown file'}</Typography>
                  }
                  secondary={
                    <Box sx={{ mt: 0.5 }}>
                      {file.description && (
                        <Typography variant="body2" color="text.secondary">
                          {file.description}
                        </Typography>
                      )}
                      {file.category && (
                        <Chip
                          label={file.category}
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      )}
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        Uploaded: {new Date(file.createdAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteFile(file.id);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      {/* File Upload Dialog */}
      <LocationFileUploadDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        locationId={id || ''}
        onSuccess={loadLocationFiles}
      />
    </Show>
  );
};